(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const initCalendar = (root) => {
    if (!root || root.dataset.rsrlReady === 'true') return;
    const lists = Array.from(root.querySelectorAll('[data-rsrl-calendar-list]'));
    if (!lists.length) return;
    root.dataset.rsrlReady = 'true';
    const items = lists.flatMap((list) => Array.from(list.querySelectorAll('[data-rsrl-course-card]')));
    const groups = Array.from(root.querySelectorAll('[data-rsrl-calendar-group]'));
    const citySelect = root.querySelector('[data-rsrl-filter-location]');
    const dateSelect = root.querySelector('[data-rsrl-filter-date]');
    const typeSelect = root.querySelector('[data-rsrl-filter-type]');
    const empty = root.querySelector('[data-rsrl-filter-empty]');
    const normalizeFilterValue = (value) => String(value || '').trim().toLocaleLowerCase('it-IT');
    const setFilteredHidden = (element, hidden) => {
      if (!element) return;
      element.hidden = hidden;
      element.classList.toggle('rsrl-filter-hidden', hidden);
      element.setAttribute('aria-hidden', String(hidden));
    };
    const itemDates = (item) => {
      const dates = [...new Set(String(item.dataset.dates || '').split('|').map((value) => value.trim()).filter(Boolean))];
      if (dates.length) return dates;
      const sortDate = String(item.dataset.date || '');
      return /^\d{8}$/.test(sortDate)
        ? [`${sortDate.slice(0, 4)}-${sortDate.slice(4, 6)}-${sortDate.slice(6, 8)}`]
        : [];
    };
    const formatDate = (value) => {
      const parts = value.split('-');
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
    };
    const populate = (select, key) => {
      if (!select || select.dataset.autoOptions !== 'true') return;
      const uniqueValues = new Map();
      items.forEach((item) => {
        const label = String(item.dataset[key] || '').trim();
        const normalized = normalizeFilterValue(label);
        if (normalized && !uniqueValues.has(normalized)) uniqueValues.set(normalized, label);
      });
      const values = [...uniqueValues.values()].sort((a, b) => a.localeCompare(b, 'it'));
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });
    };
    populate(citySelect, 'city');
    populate(typeSelect, 'type');
    const populateDates = () => {
      if (!dateSelect) return;
      const selectedDate = dateSelect.value;
      const city = normalizeFilterValue(citySelect?.value);
      const type = normalizeFilterValue(typeSelect?.value);
      const values = [
        ...new Set(
          items
            .filter((item) =>
              (!city || normalizeFilterValue(item.dataset.city) === city) &&
              (!type || normalizeFilterValue(item.dataset.type) === type)
            )
            .flatMap(itemDates)
        ),
      ].sort();
      while (dateSelect.options.length > 1) dateSelect.remove(1);
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = formatDate(value);
        dateSelect.appendChild(option);
      });
      dateSelect.value = values.includes(selectedDate) ? selectedDate : '';
    };
    if (root.dataset.rsrlSort !== 'manual') {
      lists.forEach((list) => {
        const listItems = items.filter((item) => item.closest('[data-rsrl-calendar-list]') === list);
        listItems.sort((a, b) => Number(a.dataset.date || Number.MAX_SAFE_INTEGER) - Number(b.dataset.date || Number.MAX_SAFE_INTEGER));
        listItems.forEach((item) => list.appendChild(item.closest('[data-rsrl-course-entry]') || item));
      });
    }
    const filter = () => {
      const city = normalizeFilterValue(citySelect?.value);
      const date = dateSelect?.value || '';
      const type = normalizeFilterValue(typeSelect?.value);
      const hasActiveFilter = Boolean(city || date || type);
      let visible = 0;
      items.forEach((item) => {
        const show =
          (!city || normalizeFilterValue(item.dataset.city) === city) &&
          (!date || itemDates(item).includes(date)) &&
          (!type || normalizeFilterValue(item.dataset.type) === type);
        const entry = item.closest('[data-rsrl-course-entry]') || item;
        setFilteredHidden(entry, !show);
        if (show) visible += 1;
      });
      groups.forEach((group) => {
        const groupItems = Array.from(group.querySelectorAll('[data-rsrl-course-card]'));
        const groupHasNoResults = groupItems.length === 0 || groupItems.every((item) => (item.closest('[data-rsrl-course-entry]') || item).hidden);
        setFilteredHidden(group, hasActiveFilter && groupHasNoResults);
      });
      setFilteredHidden(empty, visible !== 0);
    };
    const updateDependentFilters = () => {
      populateDates();
      filter();
    };
    citySelect?.addEventListener('change', updateDependentFilters);
    typeSelect?.addEventListener('change', updateDependentFilters);
    dateSelect?.addEventListener('change', filter);
    populateDates();
    filter();
  };

  const initGallery = (root) => {
    const main = root.querySelector('[data-rsrl-main-image], .rsrl-product__media-main img');
    const thumbs = root.querySelectorAll('[data-rsrl-thumb]');
    if (!main || !thumbs.length) return;
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      main.src = thumb.dataset.src;
      main.srcset = thumb.dataset.srcset || '';
      main.alt = thumb.dataset.alt || '';
      thumbs.forEach((item) => item.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    }));
  };

  const initProduct = (root) => {
    if (!root || root.dataset.rsrlReady === 'true') return;
    root.dataset.rsrlReady = 'true';
    initGallery(root);
    const dataNode = root.querySelector('[data-rsrl-variants]');
    if (!dataNode) return;
    let variants = [];
    try { variants = JSON.parse(dataNode.textContent); } catch (error) { return; }
    const form = root.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('[name="id"]');
    const submit = form?.querySelector('[type="submit"][name="add"]');
    const submitText = submit?.querySelector('[data-rsrl-submit-text]');
    const price = root.querySelector('[data-rsrl-product-price]');
    const comparePrice = root.querySelector('[data-rsrl-compare-price]');
    const availability = root.querySelector('[data-rsrl-availability]');
    const sku = root.querySelector('[data-rsrl-sku]');
    const quantity = form?.querySelector('[name="quantity"]');
    const selectedOptions = () => Array.from(root.querySelectorAll('[data-rsrl-option-group]')).map((group) => group.querySelector('input:checked')?.value || group.querySelector('select')?.value || '');
    const update = () => {
      const options = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) return;
      if (idInput) { idInput.value = variant.id; idInput.disabled = !variant.available; }
      if (price) price.textContent = variant.price_formatted;
      if (comparePrice) { comparePrice.textContent = variant.compare_at_price_formatted || ''; comparePrice.hidden = !variant.compare_at_price_formatted; }
      if (sku) sku.textContent = variant.sku || '';
      if (availability) { availability.textContent = variant.availability_label; availability.dataset.soldOut = String(!variant.available); }
      if (quantity && variant.inventory_management && variant.inventory_policy === 'deny') quantity.max = Math.max(variant.inventory_quantity, 1);
      else if (quantity) quantity.removeAttribute('max');
      if (submit) { submit.disabled = !variant.available; submit.setAttribute('aria-disabled', String(!variant.available)); }
      if (submitText) submitText.textContent = variant.available ? submitText.dataset.available : submitText.dataset.soldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    root.querySelectorAll('[data-rsrl-option-group] input, [data-rsrl-option-group] select').forEach((control) => control.addEventListener('change', update));
    update();
  };

  const initReviewCarousel = (root) => {
    if (!root || root.dataset.rsrlReviewsReady === 'true') return;
    root.dataset.rsrlReviewsReady = 'true';
    const track = root.querySelector('[data-rsrl-reviews-track]');
    const previous = root.querySelector('[data-rsrl-reviews-prev]');
    const next = root.querySelector('[data-rsrl-reviews-next]');
    if (!track) return;
    const move = (direction) => {
      const card = track.querySelector('.rsrl-review-card');
      const distance = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * .85;
      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    };
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-rsrl-calendar]').forEach(initCalendar);
    scope.querySelectorAll('[data-rsrl-product]').forEach(initProduct);
    scope.querySelectorAll('[data-rsrl-reviews]').forEach(initReviewCarousel);
  };
  ready(() => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
